// src/pages/admin/AdminLogin.js
import React, { useState } from "react";
import styled from "styled-components";
import logo from "../../assets/logos/capacitacion-logo-v2.png";
import crmMexicoImg from "../../assets/images/crmexico.png"; // La imagen que quieres mantener
import fondo from "../../assets/images/CNC-Home.png";

const API_URL = process.env.REACT_APP_API_URL || "https://crm-backend-5tk6.onrender.com";

const Wrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: url(${fondo}) no-repeat center center / cover;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 720px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(0,0,0,0.28);
  display: flex;
  flex-direction: ${props => props.$isMobile ? 'column' : 'row'};
  background: #ffffff;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  flex: 0.9;
  min-height: 140px;

  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-height: 140px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const FormContainer = styled.div`
  flex: 1.1;
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (max-width: 768px) {
    flex: 1 1 100%;
    padding: 24px 28px;
  }
`;

const LogoWrap = styled.div`
  text-align: center;
  margin-bottom: 4px;
  img {
    height: 72px;
    object-fit: contain;
  }
`;

const Title = styled.h2`
  color: #1a1a1a;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-align: center;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #444;
  font-size: 0.85rem;
  max-width: 380px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.78rem;
  }
`;

const Divider = styled.div`
  width: 40px;
  height: 3px;
  background: #2e7d32;
  border-radius: 2px;
  margin: 4px auto 12px;
`;

const Label = styled.label`
  font-size: 0.82rem;
  color: #444;
  font-weight: 500;
  display: block;
  margin-bottom: 6px;
  width: 100%;
  text-align: left;
`;

const InputWrap = styled.div`
  position: relative;
  margin-bottom: 16px;
  width: 100%;
  svg, span.icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
    font-size: 1rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 14px 11px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.92rem;
  box-sizing: border-box;
  color: #333;
  background: #fafafa;
  transition: border 0.2s;
  &:focus {
    outline: none;
    border-color: #2e7d32;
    background: #fff;
  }
  &::placeholder { color: #bbb; }
`;

const EyeBtn = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  font-size: 1rem;
  padding: 0;
  &:hover { color: #555; }
`;

const RememberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  font-size: 0.82rem;
  width: 100%;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #555;
  cursor: pointer;
  input { accent-color: #2e7d32; }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: #2e7d32;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
  &:hover:not(:disabled) { background: #1b5e20; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Error = styled.p`
  color: #c8102e;
  font-size: 0.82rem;
  text-align: center;
  margin-bottom: 12px;
  background: #fdecea;
  border-radius: 6px;
  padding: 8px;
  width: 100%;
`;

const FooterNote = styled.p`
  text-align: center;
  font-size: 0.78rem;
  color: #aaa;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
`;

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error al iniciar sesión.");
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_nombre", data.nombre);
      onLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        {/* LADO IZQUIERDO - IMAGEN */}
        <ImageContainer>
          <Image src={crmMexicoImg} alt="Cruz Roja Mexicana" />
        </ImageContainer>

        {/* LADO DERECHO - FORMULARIO */}
        <FormContainer>
          <LogoWrap>
            <img src={logo} alt="Capacitación CNCW" />
          </LogoWrap>

          <Title>Panel de Administración</Title>
          <Subtitle>
            Plataforma de gestión administrativa.
          </Subtitle>
          <Divider />

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Label>Correo electrónico</Label>
            <InputWrap>
              <span className="icon">✉️</span>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="correo@cruzrojamexicana.org.mx"
                required
              />
            </InputWrap>

            <Label>Contraseña</Label>
            <InputWrap>
              <span className="icon">🔒</span>
              <Input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <EyeBtn type="button" onClick={() => setShowPass(s => !s)}>
                {showPass ? "🙈" : "👁️"}
              </EyeBtn>
            </InputWrap>

            <RememberRow>
              <CheckLabel>
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                Recordarme
              </CheckLabel>
            </RememberRow>

            {error && <Error>⚠️ {error}</Error>}

            <Button type="submit" disabled={loading}>
              {loading ? "Iniciando sesión..." : <>Ingresar →</>}
            </Button>
          </form>

          <FooterNote>
            🛡️ Acceso restringido solo para personal autorizado.
          </FooterNote>
        </FormContainer>
      </Card>
    </Wrapper>
  );
}